import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
} from "@/components/ui/sheet";
import { useNavigate } from "react-router-dom";
import {
  Home,
  User,
  Bell,
  CalendarCheck,
  Wallet,
  Megaphone,
  Trophy,
  Users,
  CalendarDays,
  ClipboardList,
  BookOpen,
  NotebookPen,
  Calendar,
  FileSignature,
  MessageSquare,
  Edit3,
  PlaneTakeoff,
  LogOut,
} from "lucide-react";
import { toast } from "sonner";
import {
  clearSessionUser,
  getSessionUser,
  getUnreadNotificationCount,
} from "@/lib/eisStore";

const LINKS = [
  { key: "dashboard", label: "Dashboard", Icon: Home, path: "/dashboard" },
  { key: "profile", label: "Profile", Icon: User, path: "/profile" },
  { key: "notifications", label: "Notifications", Icon: Bell, path: "/notifications" },
  { key: "attendance", label: "Attendance", Icon: CalendarCheck, path: "/attendance", badge: { text: "3 absent", tone: "red" } },
  { key: "fees", label: "Fees", Icon: Wallet, path: "/fees", badge: { text: "₹25,000 due", tone: "red" } },
  { key: "circulars", label: "Circulars", Icon: Megaphone, path: "/circulars" },
  { key: "result", label: "Result", Icon: Trophy, path: "/result" },
  { key: "timetable", label: "Timetable", Icon: CalendarDays, path: "/timetable" },
  { key: "exam-schedule", label: "Exam Schedule", Icon: ClipboardList, path: "/exam-schedule", badge: { text: "1 upcoming", tone: "amber" } },
  { key: "syllabus", label: "Syllabus", Icon: BookOpen, path: "/syllabus" },
  { key: "homework", label: "Homework", Icon: NotebookPen, path: "/homework" },
  { key: "calendar", label: "Calendar", Icon: Calendar, path: "/calendar" },
  { key: "consent-forms", label: "Consent Forms", Icon: FileSignature, path: "/consent-forms" },
  { key: "remarks", label: "Remarks", Icon: MessageSquare, path: "/remarks" },
  { key: "change-of-information", label: "Change of Information", Icon: Edit3, path: "/change-of-information" },
];

const TEACHER_LINKS = [
  { key: "dashboard", label: "Dashboard", Icon: Home, path: "/dashboard" },
  { key: "profile", label: "Profile", Icon: User, path: "/profile" },
  { key: "student-data", label: "Student Data", Icon: ClipboardList, path: "/student-data" },
  { key: "notifications", label: "Notifications", Icon: Bell, path: "/notifications" },
  { key: "apply-leave", label: "Apply for Leave", Icon: PlaneTakeoff, path: "/apply-leave" },
  { key: "result", label: "Result", Icon: Trophy, path: "/result" },
  { key: "timetable", label: "Timetable", Icon: CalendarDays, path: "/timetable" },
  { key: "exam-schedule", label: "Exam Schedule", Icon: ClipboardList, path: "/exam-schedule" },
  { key: "syllabus", label: "Syllabus", Icon: BookOpen, path: "/syllabus" },
  { key: "homework", label: "Homework", Icon: NotebookPen, path: "/homework" },
  { key: "consent-forms", label: "Consent Forms", Icon: FileSignature, path: "/consent-forms" },
  { key: "remarks", label: "Remarks", Icon: MessageSquare, path: "/remarks" },
];

const ADMIN_LINKS = [
  { key: "profile", label: "Profile", Icon: User, path: "/profile" },
  { key: "notifications", label: "Notifications", Icon: Bell, path: "/notifications" },
  { key: "circulars", label: "Circulars", Icon: Megaphone, path: "/circulars" },
  { key: "student-data", label: "Student Data", Icon: ClipboardList, path: "/student-data" },
  { key: "edit-homepage", label: "Edit Homepage", Icon: Edit3, path: "/edit-homepage" },
];

const MANAGEMENT_LINKS = [
  { key: "dashboard", label: "Dashboard", Icon: Home, path: "/dashboard" },
  { key: "profile", label: "Profile", Icon: User, path: "/profile" },
  { key: "notifications", label: "Notifications", Icon: Bell, path: "/notifications" },
  { key: "circulars", label: "Circulars", Icon: Megaphone, path: "/circulars" },
  { key: "student-data", label: "Student Data", Icon: ClipboardList, path: "/student-data" },
  { key: "teacher-data", label: "Teacher Data", Icon: Users, path: "/teacher-data" },
  { key: "fees", label: "Fees", Icon: Wallet, path: "/fees" },
];

const TONE_CLASSES = {
  red: "bg-red-100 text-red-700 border-red-200",
  amber: "bg-amber-100 text-amber-700 border-amber-200",
  green: "bg-green-100 text-green-700 border-green-200",
  blue: "bg-blue-100 text-blue-700 border-blue-200",
};

const LOGO_URL = "/img/logo.jpg";

export default function AppSidebar({ open, onOpenChange }) {
  const navigate = useNavigate();
  const user = getSessionUser();
  const teacherLinks =
    user?.role === "teacher" && user?.assignedClassKey
      ? TEACHER_LINKS
      : user?.role === "teacher"
        ? TEACHER_LINKS.filter((link) => link.key !== "attendance")
        : [];
  const links =
    user?.role === "teacher"
      ? teacherLinks
      : user?.role === "admin"
        ? ADMIN_LINKS
        : user?.role === "management"
          ? MANAGEMENT_LINKS
        : LINKS;
  const unreadCount = getUnreadNotificationCount(user);

  const go = (path) => {
    onOpenChange(false);
    navigate(path);
  };

  const logout = () => {
    clearSessionUser();
    toast.success("Logged out");
    navigate("/");
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        side="left"
        className="w-80 p-0 bg-white flex flex-col"
        data-testid="app-sidebar"
      >
        <SheetHeader className="p-5 eis-header text-white">
          <div className="flex items-center gap-3">
            <div className="h-12 w-12 rounded-full bg-white flex items-center justify-center flex-shrink-0 overflow-hidden">
              <img src={LOGO_URL} alt="EIS" className="h-12 w-12 object-cover" />
            </div>
            <div className="min-w-0 text-left">
              <SheetTitle className="text-white text-base truncate">
                {user?.name || "Guest"}
              </SheetTitle>
              <SheetDescription className="text-white/80 text-xs capitalize truncate">
                {user?.role || "user"} · {user?.email}
              </SheetDescription>
              {unreadCount > 0 && (
                <p className="text-[11px] mt-1 text-white/90 font-semibold">
                  {unreadCount} unread notification{unreadCount === 1 ? "" : "s"}
                </p>
              )}
            </div>
          </div>
        </SheetHeader>

        <div className="flex-1 overflow-y-auto py-2">
          {links.map(({ key, label, Icon, path, badge }) => (
            <button
              key={key}
              data-testid={`sidebar-link-${key}`}
              onClick={() => go(path)}
              className="w-full flex items-center gap-3 px-5 py-3 text-slate-700 hover:bg-slate-100 transition text-left"
            >
              <Icon size={20} className="text-[#0b1f7a] flex-shrink-0" />
              <span className="font-medium flex-1 truncate">{label}</span>
              {badge && (
                <span
                  data-testid={`sidebar-badge-${key}`}
                  className={`text-[10px] font-semibold px-2 py-0.5 rounded-full border whitespace-nowrap ${
                    TONE_CLASSES[badge.tone] || TONE_CLASSES.blue
                  }`}
                >
                  {badge.text}
                </span>
              )}
            </button>
          ))}
        </div>

        <div className="border-t border-slate-200">
          <button
            data-testid="sidebar-logout-btn"
            onClick={logout}
            className="w-full flex items-center gap-3 px-5 py-3 text-red-600 hover:bg-red-50 transition text-left"
          >
            <LogOut size={20} />
            <span className="font-medium">Log out</span>
          </button>
        </div>
      </SheetContent>
    </Sheet>
  );
}
